import { action, computed, makeObservable, observable } from 'mobx'
import { AccountMergeRequestStore } from '../AccountMergeRequestStore'
import { Id } from '../../../types/definitions'
import { task } from 'mobx-task'
import { AccountMergeRequestItemViewModel } from '../AccountMergeRequestItemViewModel'
import { AlertFactory, AlertState } from '../../../alerts/Alert'

export class MergeAccountsDialogViewModel {
  /**
   * Whether the dialog should be shown.
   */
  @observable
  showing = false

  /**
   * The account merge request to accept.
   */
  @observable
  private _accountMergeRequest: AccountMergeRequestItemViewModel | null = null

  constructor(
    private readonly _store: AccountMergeRequestStore,
    private readonly _refreshItems: () => Promise<void>,
    private readonly _newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  /**
   * The customer's display name.
   */
  @computed
  get displayName() {
    return (
      this._accountMergeRequest?.displayName ??
      this._accountMergeRequest?.email ??
      '(Not Provided)'
    )
  }

  /**
   * Inflates the dialog for the given account merge request.
   */
  @action.bound
  show(accountMergeRequest: AccountMergeRequestItemViewModel) {
    this._accountMergeRequest = accountMergeRequest
    this.showing = true
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.showing = false
  }

  /**
   * Performs the merge request.
   */
  link = task.resolved(() => {
    if (!this._accountMergeRequest) {
      return
    }

    return this._store
      .link(Id.assert(this._accountMergeRequest.id))
      .then(this._refreshItems)
      .then(this.close)
      .then(() =>
        this._newAlert()
          .saying('The accounts were successfully linked.')
          .withState(AlertState.Success)
          .autoDismiss()
          .show(),
      )
  })
}
